import { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  ChevronRight, 
  ChevronDown, 
  ExternalLink, 
  Grid3X3,
  List,
  Search,
  FolderPlus,
  Upload,
  Loader2,
  Home
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import folderIcon from '@/assets/folder-icon.png';
// File type icons
import bmpIcon from '@/assets/file-icons/bmp.png';
import docIcon from '@/assets/file-icons/doc.png';
import fileIcon from '@/assets/file-icons/file.png';
import pngIcon from '@/assets/file-icons/png.png';
import pptIcon from '@/assets/file-icons/ppt.png';
import psIcon from '@/assets/file-icons/ps.png';
import psdIcon from '@/assets/file-icons/psd.png';
import txtIcon from '@/assets/file-icons/txt.png';
import xlsIcon from '@/assets/file-icons/xls.png';
import zipIcon from '@/assets/file-icons/zip.png';
import jpgIcon from '@/assets/file-icons/jpg.png';
import pdfIcon from '@/assets/pdf-icon.png';
import csvIcon from '@/assets/csv-icon.png';
import wordIcon from '@/assets/word-icon.png';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface Document {
  id: string;
  name: string;
  description: string | null;
  folder_path: string | null;
  google_drive_url: string | null;
  google_drive_file_id: string | null;
  mime_type: string | null;
  file_size: number | null;
  created_at: string;
}

interface FolderNode {
  name: string;
  path: string;
  documents: Document[];
  subfolders: Record<string, FolderNode>;
}

interface DocumentsTabContentProps {
  ptaId: string | null;
  isReadOnly: boolean;
  isGoogleConnected: boolean;
  isDriveEnabled: boolean;
  onConnectGoogle: () => void;
}

// Get file icon based on file name extension and mime type
const getFileIcon = (mimeType: string | null, size: 'sm' | 'md' | 'lg' = 'md', fileName?: string) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-12 w-12'
  };
  
  // Extract extension from filename
  const extension = fileName?.split('.').pop()?.toLowerCase();
  
  // Map extensions to icons
  const extensionIconMap: Record<string, string> = {
    'bmp': bmpIcon,
    'png': pngIcon,
    'jpg': jpgIcon,
    'jpeg': jpgIcon,
    'gif': pngIcon,
    'webp': pngIcon,
    'doc': docIcon,
    'docx': wordIcon,
    'ppt': pptIcon,
    'pptx': pptIcon,
    'ps': psIcon,
    'psd': psdIcon,
    'txt': txtIcon,
    'pdf': pdfIcon,
    'csv': csvIcon,
    'xls': xlsIcon,
    'xlsx': xlsIcon,
    'zip': zipIcon,
    'rar': zipIcon,
    '7z': zipIcon,
  };
  
  // Check by extension first
  if (extension && extensionIconMap[extension]) {
    return (
      <img 
        src={extensionIconMap[extension]} 
        alt={extension.toUpperCase()} 
        className={cn(sizeClasses[size])}
      />
    );
  }
  
  // Fallback to mime type based matching
  if (mimeType) {
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
      return <img src={csvIcon} alt="Spreadsheet" className={cn(sizeClasses[size])} />;
    }
    if (mimeType.includes('document') || mimeType.includes('word')) {
      return <img src={wordIcon} alt="Document" className={cn(sizeClasses[size])} />;
    }
    if (mimeType.includes('image')) {
      return <img src={pngIcon} alt="Image" className={cn(sizeClasses[size])} />;
    }
    if (mimeType.includes('pdf')) {
      return <img src={pdfIcon} alt="PDF" className={cn(sizeClasses[size])} />;
    }
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) {
      return <img src={pptIcon} alt="Presentation" className={cn(sizeClasses[size])} />;
    }
    if (mimeType.includes('text/plain')) {
      return <img src={txtIcon} alt="Text" className={cn(sizeClasses[size])} />;
    }
  }
  
  // Default generic file icon
  return <img src={fileIcon} alt="File" className={cn(sizeClasses[size])} />;
};

// Custom folder icon component using uploaded image
const FolderIcon = ({ size = 'md', className }: { size?: 'sm' | 'md' | 'lg'; className?: string }) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-12 w-12'
  };
  
  return (
    <img 
      src={folderIcon} 
      alt="Folder" 
      className={cn(sizeClasses[size], className)}
    />
  );
};

import googleDriveLogo from '@/assets/google-drive-logo.png';

export function DocumentsTabContent({ ptaId, isReadOnly, isGoogleConnected, isDriveEnabled, onConnectGoogle }: DocumentsTabContentProps) {
  // Show not connected state if Google Drive is not set up
  if (!isGoogleConnected || !isDriveEnabled) {
    return (
      <Card className="max-w-md mx-auto mt-8">
        <CardHeader className="text-center">
          <img src={googleDriveLogo} alt="Google Drive" className="h-16 w-16 mx-auto mb-4" />
          <CardTitle>Google Drive Not Connected</CardTitle>
          <CardDescription>
            Connect your PTA's Google Drive account to manage and share documents with your team.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <a href="/pta-admin?tab=integrations" className="text-primary hover:underline">
            Go to Integrations to connect Google Drive
          </a>
        </CardContent>
      </Card>
    );
  }
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['/']));
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPath, setCurrentPath] = useState('/');
  const [isPtaAdmin, setIsPtaAdmin] = useState(false);
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const syncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { toast } = useToast();

  // Auto-sync on mount and periodic refresh
  useEffect(() => {
    if (ptaId) {
      checkPtaAdmin();
      performSync(true);
      
      syncIntervalRef.current = setInterval(() => {
        performSync(false);
      }, 60000);
    }

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [ptaId]);

  const checkPtaAdmin = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !ptaId) return;

    const { data } = await supabase
      .from('members')
      .select('is_pta_admin')
      .eq('user_id', user.id)
      .eq('pta_id', ptaId)
      .maybeSingle();

    setIsPtaAdmin(data?.is_pta_admin || false);
  };

  const fetchDocuments = async () => {
    if (!ptaId) return;
    
    setLoading(true);
    const { data, error } = await supabase
      .from('pta_documents')
      .select('*')
      .eq('pta_id', ptaId)
      .order('folder_path')
      .order('name');

    if (!error && data) {
      setDocuments(data);
    }
    setLoading(false);
  };

  // Build folder tree structure
  const buildFolderTree = (): FolderNode => {
    const root: FolderNode = {
      name: 'Documents',
      path: '/',
      documents: [],
      subfolders: {}
    };

    documents.forEach(doc => {
      const folderPath = doc.folder_path || '/';
      const pathParts = folderPath.split('/').filter(Boolean);
      
      let current = root;
      let currentPathBuilder = '/';

      pathParts.forEach(part => {
        currentPathBuilder = currentPathBuilder === '/' ? `/${part}` : `${currentPathBuilder}/${part}`;
        if (!current.subfolders[part]) {
          current.subfolders[part] = {
            name: part,
            path: currentPathBuilder,
            documents: [],
            subfolders: {}
          };
        }
        current = current.subfolders[part];
      });

      if (folderPath === '/') {
        root.documents.push(doc);
      } else {
        current.documents.push(doc);
      }
    });

    return root;
  };

  const toggleFolder = (path: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const navigateToFolder = (path: string) => {
    setCurrentPath(path);
    setExpandedFolders(prev => {
      const next = new Set(prev);
      next.add(path);
      // Also expand all parent paths
      const parts = path.split('/').filter(Boolean);
      let buildPath = '';
      parts.forEach(part => {
        buildPath = buildPath ? `${buildPath}/${part}` : `/${part}`;
        next.add(buildPath);
      });
      next.add('/');
      return next;
    });
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Filter documents based on search query
  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents;
    const query = searchQuery.toLowerCase();
    return documents.filter(doc => 
      doc.name.toLowerCase().includes(query) ||
      (doc.description && doc.description.toLowerCase().includes(query))
    );
  }, [documents, searchQuery]);

  // Get current folder node from tree
  const getCurrentFolderNode = (): FolderNode => {
    const tree = buildFolderTree();
    if (currentPath === '/') return tree;
    
    const parts = currentPath.split('/').filter(Boolean);
    let current = tree;
    for (const part of parts) {
      if (current.subfolders[part]) {
        current = current.subfolders[part];
      }
    }
    return current;
  };

  // Get items for current folder (for content pane)
  const getCurrentFolderItems = () => {
    const folder = getCurrentFolderNode();
    const subfolders = Object.values(folder.subfolders);
    const files = folder.documents.filter(d => d.name !== '.folder');
    
    // Filter if search is active
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const filteredFiles = files.filter(doc => 
        doc.name.toLowerCase().includes(query) ||
        (doc.description && doc.description.toLowerCase().includes(query))
      );
      const filteredFolders = subfolders.filter(f => 
        f.name.toLowerCase().includes(query)
      );
      return { subfolders: filteredFolders, files: filteredFiles };
    }
    
    return { subfolders, files };
  };

  const canAddFolder = () => {
    if (isReadOnly) return false;
    if (currentPath === '/') return isPtaAdmin;
    return true;
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !ptaId) return;

    setCreatingFolder(true);
    
    const folderPath = currentPath === '/' ? `/${newFolderName.trim()}` : `${currentPath}/${newFolderName.trim()}`;
    
    const { data: { user } } = await supabase.auth.getUser();
    
    const { error } = await supabase
      .from('pta_documents')
      .insert({
        pta_id: ptaId,
        name: '.folder',
        folder_path: folderPath,
        uploaded_by: user?.id
      });

    if (error) {
      toast({
        title: "Error",
        description: "Failed to create folder",
        variant: "destructive"
      });
    } else {
      toast({
        title: "Folder created",
        description: `Created folder "${newFolderName.trim()}"`
      });
      setNewFolderName('');
      setNewFolderDialogOpen(false);
      fetchDocuments();
    }
    setCreatingFolder(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadDialogOpen(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !ptaId) return;

    setUploading(true);
    try {
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          const base64 = result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(selectedFile);
      const fileContent = await base64Promise;

      const { data, error } = await supabase.functions.invoke('google-drive', {
        body: {
          action: 'upload',
          ptaId,
          fileName: selectedFile.name,
          fileContent,
          mimeType: selectedFile.type || 'application/octet-stream',
          folderPath: currentPath,
          description: uploadDescription,
        },
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      toast({
        title: "File uploaded",
        description: `"${selectedFile.name}" uploaded to Google Drive`
      });

      setSelectedFile(null);
      setUploadDescription('');
      setUploadDialogOpen(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fetchDocuments();
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload file",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleOpenDocument = (doc: Document) => {
    if (doc.google_drive_url) {
      window.open(doc.google_drive_url, '_blank', 'noopener,noreferrer');
    }
  };

  const performSync = async (showLoadingState: boolean = true) => {
    if (!ptaId || syncing) return;
    
    if (showLoadingState) {
      setSyncing(true);
      setLoading(true);
    }
    
    try {
      const { data, error } = await supabase.functions.invoke('google-drive', {
        body: {
          action: 'sync',
          ptaId,
        },
      });

      if (error) {
        if (error.message?.includes('Google Drive not connected')) {
          await fetchDocuments();
          return;
        }
        throw error;
      }

      if (data?.error) {
        if (data.error.includes('Google Drive not connected')) {
          await fetchDocuments();
          return;
        }
        throw new Error(data.error);
      }

      setLastSyncTime(new Date());
      await fetchDocuments();
      
      if (showLoadingState && (data.addedCount > 0 || data.removedCount > 0 || data.updatedCount > 0)) {
        toast({
          title: "Synced with Google Drive",
          description: `Added: ${data.addedCount}, Removed: ${data.removedCount}, Updated: ${data.updatedCount}`
        });
      }
    } catch (error: any) {
      console.error('Sync error:', error);
      if (showLoadingState) {
        toast({
          title: "Sync issue",
          description: "Could not sync with Google Drive. Showing cached documents.",
          variant: "destructive"
        });
        await fetchDocuments();
      }
    } finally {
      if (showLoadingState) {
        setSyncing(false);
        setLoading(false);
      }
    }
  };

  const canUpload = () => {
    if (isReadOnly) return false;
    if (currentPath === '/') return isPtaAdmin;
    return true;
  };

  // Breadcrumb navigation
  const getBreadcrumbs = () => {
    const parts = currentPath.split('/').filter(Boolean);
    const crumbs = [{ name: 'Documents', path: '/' }];
    let buildPath = '';
    parts.forEach(part => {
      buildPath += `/${part}`;
      crumbs.push({ name: part, path: buildPath });
    });
    return crumbs;
  };

  // Render folder tree for left sidebar navigation
  const renderNavigationTree = (folder: FolderNode, depth: number = 0) => {
    const isExpanded = expandedFolders.has(folder.path);
    const isSelected = currentPath === folder.path;
    const hasSubfolders = Object.keys(folder.subfolders).length > 0;
    
    return (
      <div key={folder.path}>
        <div
          className={cn(
            "flex items-center gap-1 py-1.5 px-2 rounded cursor-pointer hover:bg-muted/50 transition-colors",
            isSelected && "bg-primary/10 text-primary font-medium"
          )}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          onClick={() => navigateToFolder(folder.path)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleFolder(folder.path);
            }}
            className="w-4 h-4 flex items-center justify-center shrink-0"
          >
            {hasSubfolders ? (
              isExpanded ? (
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-3 w-3 text-muted-foreground" />
              )
            ) : (
              <span className="w-3" />
            )}
          </button>
          <FolderIcon size="sm" className="shrink-0" />
          <span className="text-sm truncate">{folder.name}</span>
        </div>

        {isExpanded && hasSubfolders && (
          <div>
            {Object.values(folder.subfolders).map(subfolder => 
              renderNavigationTree(subfolder, depth + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  // Render grid view (large icons) for content pane
  const renderGridView = () => {
    const { subfolders, files } = getCurrentFolderItems();
    const hasItems = subfolders.length > 0 || files.length > 0;

    if (!hasItems) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <FolderIcon size="lg" className="mb-4 opacity-50" />
          <p className="font-medium">This folder is empty</p>
          <p className="text-sm mt-1">Upload files or create subfolders to get started</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 p-4">
        {/* Folders first */}
        {subfolders.map(folder => (
          <div
            key={folder.path}
            className="flex flex-col items-center p-4 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors group"
            onDoubleClick={() => navigateToFolder(folder.path)}
          >
            <FolderIcon size="lg" />
            <span className="text-sm font-medium mt-2 text-center truncate w-full">
              {folder.name}
            </span>
            <span className="text-xs text-muted-foreground">
              {Object.keys(folder.subfolders).length + folder.documents.filter(d => d.name !== '.folder').length} items
            </span>
          </div>
        ))}
        
        {/* Files */}
        {files.map(doc => (
          <div
            key={doc.id}
            className="flex flex-col items-center p-4 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors group"
            onDoubleClick={() => handleOpenDocument(doc)}
          >
            {getFileIcon(doc.mime_type, 'lg', doc.name)}
            <span className="text-sm font-medium mt-2 text-center truncate w-full">
              {doc.name}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatFileSize(doc.file_size)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Render list/details view for content pane
  const renderListView = () => {
    const { subfolders, files } = getCurrentFolderItems();
    const hasItems = subfolders.length > 0 || files.length > 0;

    if (!hasItems) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <FolderIcon size="lg" className="mb-4 opacity-50" />
          <p className="font-medium">This folder is empty</p>
          <p className="text-sm mt-1">Upload files or create subfolders to get started</p>
        </div>
      );
    }

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[400px]">Name</TableHead>
            <TableHead>Date Modified</TableHead>
            <TableHead>Type</TableHead>
            <TableHead className="text-right">Size</TableHead>
            <TableHead className="w-[60px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* Folders first */}
          {subfolders.map(folder => (
            <TableRow 
              key={folder.path} 
              className="cursor-pointer"
              onDoubleClick={() => navigateToFolder(folder.path)}
            >
              <TableCell className="font-medium">
                <div className="flex items-center gap-3">
                  <FolderIcon size="sm" />
                  <span>{folder.name}</span>
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground">-</TableCell>
              <TableCell className="text-muted-foreground">Folder</TableCell>
              <TableCell className="text-right text-muted-foreground">-</TableCell>
              <TableCell></TableCell>
            </TableRow>
          ))}
          
          {/* Files */}
          {files.map(doc => (
            <TableRow 
              key={doc.id} 
              className="cursor-pointer group"
              onDoubleClick={() => handleOpenDocument(doc)}
            >
              <TableCell className="font-medium">
                <div className="flex items-center gap-3">
                  {getFileIcon(doc.mime_type, 'sm', doc.name)}
                  <span className="truncate max-w-[300px]">{doc.name}</span>
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {format(new Date(doc.created_at), 'MMM d, yyyy')}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {doc.mime_type?.split('/')[1]?.toUpperCase() || 'File'}
              </TableCell>
              <TableCell className="text-right text-muted-foreground">
                {formatFileSize(doc.file_size)}
              </TableCell>
              <TableCell>
                {doc.google_drive_url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenDocument(doc);
                    }}
                    title="Open in Google Drive"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const folderTree = buildFolderTree();
  const breadcrumbs = getBreadcrumbs();

  if (loading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p>Loading documents...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>PTA Documents</CardTitle>
            <CardDescription>
              {isReadOnly ? 'Browse shared PTA documents (read-only)' : 'Browse and manage shared PTA documents'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {syncing && (
              <span className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Syncing...
              </span>
            )}
            
            {canAddFolder() && (
              <Dialog open={newFolderDialogOpen} onOpenChange={setNewFolderDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <FolderPlus className="h-4 w-4 mr-2" />
                    New Folder
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Folder</DialogTitle>
                    <DialogDescription>
                      {currentPath === '/' 
                        ? 'Create a new root folder (Admin only)' 
                        : `Create a subfolder in "${currentPath}"`}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="folder-name">Folder Name</Label>
                      <Input
                        id="folder-name"
                        placeholder="Enter folder name"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newFolderName.trim()) {
                            handleCreateFolder();
                          }
                        }}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setNewFolderDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleCreateFolder} 
                        disabled={!newFolderName.trim() || creatingFolder}
                      >
                        {creatingFolder ? 'Creating...' : 'Create Folder'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            
            {canUpload() && (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Upload dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={(open) => {
          if (!open) {
            setSelectedFile(null);
            setUploadDescription('');
            if (fileInputRef.current) fileInputRef.current.value = '';
          }
          setUploadDialogOpen(open);
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Document</DialogTitle>
              <DialogDescription>
                Upload "{selectedFile?.name}" to Google Drive{currentPath !== '/' ? ` in folder "${currentPath}"` : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="p-4 bg-muted rounded-lg flex items-center gap-3">
                {selectedFile && getFileIcon(selectedFile.type, 'md', selectedFile.name)}
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{selectedFile?.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedFile && formatFileSize(selectedFile.size)}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="upload-description">Description (optional)</Label>
                <Input
                  id="upload-description"
                  placeholder="Add a description for this file"
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
                  Cancel
                </Button>
                <Button onClick={handleUpload} disabled={uploading}>
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    'Upload to Drive'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Search bar */}
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {/* Windows Explorer-style layout */}
        <div className="border border-border rounded-lg overflow-hidden bg-background">
          {/* Toolbar */}
          <div className="flex items-center justify-between px-4 py-2 bg-muted/30 border-b border-border">
            {/* Breadcrumb navigation */}
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((crumb, index) => (
                  <BreadcrumbItem key={crumb.path}>
                    {index === breadcrumbs.length - 1 ? (
                      <BreadcrumbPage className="flex items-center gap-2">
                        {index === 0 && <Home className="h-3.5 w-3.5" />}
                        {crumb.name}
                      </BreadcrumbPage>
                    ) : (
                      <>
                        <BreadcrumbLink 
                          onClick={() => navigateToFolder(crumb.path)}
                          className="flex items-center gap-2 cursor-pointer hover:text-foreground"
                        >
                          {index === 0 && <Home className="h-3.5 w-3.5" />}
                          {crumb.name}
                        </BreadcrumbLink>
                        <BreadcrumbSeparator />
                      </>
                    )}
                  </BreadcrumbItem>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
            
            {/* View mode toggle */}
            <div className="flex items-center border border-border rounded-md p-0.5 bg-background">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setViewMode('grid')}
                title="Large icons"
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setViewMode('list')}
                title="Details"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Main content area with navigation pane */}
          <div className="flex min-h-[400px]">
            {/* Left: Navigation pane (folder tree) */}
            <div className="w-56 border-r border-border bg-muted/20 overflow-y-auto">
              <div className="py-2">
                {renderNavigationTree(folderTree)}
              </div>
            </div>

            {/* Right: Content pane */}
            <div className="flex-1 overflow-auto">
              {viewMode === 'grid' ? renderGridView() : renderListView()}
            </div>
          </div>
        </div>

        {/* Last sync info */}
        {lastSyncTime && (
          <p className="text-xs text-muted-foreground mt-3 text-right">
            Last synced: {format(lastSyncTime, 'MMM d, yyyy h:mm a')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
